/**
 * Integration Status Page - DRR-233 Track D
 *
 * Operations health dashboard for integration primitives.
 * Shows green/yellow/red status indicators for all system integrations.
 * Critical for incident response and monitoring.
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Card,
  Text,
  Badge,
  Group,
  Stack,
  Button,
  Table,
  Alert,
  ActionIcon,
  Tooltip,
  Progress,
  Tabs,
  SimpleGrid,
  RingProgress,
  ThemeIcon,
  Loader,
} from '@mantine/core';
import {
  IconRefresh,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconPlugConnected,
  IconCircleOff,
  IconClock,
  IconShieldCheck,
  IconKey,
  IconActivity,
} from '@tabler/icons-react';
import {
  useGetUnhealthyIntegrationsOpsQuery,
  useGetIntegrationsWithOpenCircuitBreakerOpsQuery,
  useGetIntegrationsNeedingCredentialRotationOpsQuery,
  useListIntegrationsQuery,
  useTestIntegrationConnectionOpsMutation,
  useUpdateCircuitBreakerStateOpsMutation,
  useRotateCredentialsOpsMutation,
  IntegrationHealthStatus,
  CircuitBreakerState,
  UpdateCircuitBreakerInput,
} from '@/generated/graphql';
import { useTenantId } from '@/auth/AuthContext';
import { notifications } from '@mantine/notifications';

function getHealthBadge(status: IntegrationHealthStatus | string) {
  switch (status) {
    case IntegrationHealthStatus.HEALTHY:
    case 'HEALTHY':
      return <Badge color="green" leftSection={<IconCheck size={12} />}>Healthy</Badge>;
    case IntegrationHealthStatus.DEGRADED:
    case 'DEGRADED':
      return <Badge color="yellow" leftSection={<IconAlertTriangle size={12} />}>Degraded</Badge>;
    case IntegrationHealthStatus.UNHEALTHY:
    case 'UNHEALTHY':
      return <Badge color="red" leftSection={<IconX size={12} />}>Unhealthy</Badge>;
    default:
      return <Badge color="gray">Unknown</Badge>;
  }
}

function getCircuitBreakerBadge(state: CircuitBreakerState | string) {
  switch (state) {
    case CircuitBreakerState.CLOSED:
    case 'CLOSED':
      return <Badge color="green" variant="outline">Closed</Badge>;
    case CircuitBreakerState.OPEN:
    case 'OPEN':
      return <Badge color="red" variant="filled">Open</Badge>;
    case CircuitBreakerState.HALF_OPEN:
    case 'HALF_OPEN':
      return <Badge color="yellow" variant="outline">Half-Open</Badge>;
    default:
      return <Badge color="gray" variant="outline">Unknown</Badge>;
  }
}

export function IntegrationStatusPage() {
  const tenantId = useTenantId();
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  // Queries
  const { data: allData, loading: allLoading, refetch: refetchAll } = useListIntegrationsQuery({
    variables: { tenantId: tenantId ?? '', limit: 100 },
    skip: !tenantId,
  });

  const { data: unhealthyData, loading: unhealthyLoading, refetch: refetchUnhealthy } = useGetUnhealthyIntegrationsOpsQuery({
    variables: { tenantId: tenantId ?? '' },
    skip: !tenantId,
  });

  const { data: circuitBreakerData, loading: circuitBreakerLoading, refetch: refetchCircuitBreaker } = useGetIntegrationsWithOpenCircuitBreakerOpsQuery({
    variables: { tenantId: tenantId ?? '' },
    skip: !tenantId,
  });

  const { data: rotationData, loading: rotationLoading, refetch: refetchRotation } = useGetIntegrationsNeedingCredentialRotationOpsQuery({
    variables: { tenantId: tenantId ?? '' },
    skip: !tenantId,
  });

  // Mutations
  const [testConnection, { loading: testing }] = useTestIntegrationConnectionOpsMutation();
  const [updateCircuitBreakerState, { loading: resetting }] = useUpdateCircuitBreakerStateOpsMutation();
  const [rotateCredentials, { loading: rotating }] = useRotateCredentialsOpsMutation();

  const handleRefreshAll = () => {
    refetchAll();
    refetchUnhealthy();
    refetchCircuitBreaker();
    refetchRotation();
  };

  const handleTestConnection = async (integrationId: string) => {
    if (!tenantId) return;
    try {
      const result = await testConnection({
        variables: { tenantId, integrationId },
      });
      if (result.data?.testIntegrationConnection?.success) {
        notifications.show({
          title: 'Connection Successful',
          message: `Response time: ${result.data.testIntegrationConnection.responseTimeMs}ms`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Connection Failed',
          message: result.data?.testIntegrationConnection?.errorMessage ?? 'Unknown error',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to test connection',
        color: 'red',
      });
    }
  };

  const handleResetCircuitBreaker = async (integrationId: string) => {
    if (!tenantId) return;
    try {
      await updateCircuitBreakerState({
        variables: {
          input: {
            integrationId,
            state: CircuitBreakerState.CLOSED,
            resetConsecutiveFailures: true,
          },
        },
      });
      notifications.show({
        title: 'Circuit Breaker Reset',
        message: 'Circuit breaker has been reset to closed state',
        color: 'green',
      });
      refetchCircuitBreaker();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to reset circuit breaker',
        color: 'red',
      });
    }
  };

  const handleRotateCredentials = async (integrationId: string) => {
    if (!tenantId) return;
    try {
      await rotateCredentials({
        variables: { tenantId, integrationId },
      });
      notifications.show({
        title: 'Credentials Rotated',
        message: 'Integration credentials have been rotated',
        color: 'green',
      });
      refetchRotation();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to rotate credentials',
        color: 'red',
      });
    }
  };

  const allIntegrations = allData?.listIntegrations?.nodes ?? [];
  const unhealthyIntegrations = unhealthyData?.getUnhealthyIntegrations ?? [];
  const openCircuitBreakers = circuitBreakerData?.getIntegrationsWithOpenCircuitBreaker ?? [];
  const needsRotation = rotationData?.getIntegrationsNeedingCredentialRotation ?? [];

  const healthyCount = allIntegrations.filter(i => i.currentHealthStatus === IntegrationHealthStatus.HEALTHY).length;
  const degradedCount = allIntegrations.filter(i => i.currentHealthStatus === IntegrationHealthStatus.DEGRADED).length;
  const unhealthyCount = allIntegrations.filter(i => i.currentHealthStatus === IntegrationHealthStatus.UNHEALTHY).length;
  const totalCount = allIntegrations.length;

  const healthPercentage = totalCount > 0 ? Math.round((healthyCount / totalCount) * 100) : 100;

  const isLoading = allLoading || unhealthyLoading || circuitBreakerLoading || rotationLoading;

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Integration Health Status</Title>
        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={handleRefreshAll}
          loading={isLoading}
        >
          Refresh All
        </Button>
      </Group>

      {/* Alerts for critical issues */}
      {openCircuitBreakers.length > 0 && (
        <Alert color="red" icon={<IconCircleOff />} mb="md">
          {openCircuitBreakers.length} integration(s) have open circuit breakers - immediate attention required
        </Alert>
      )}

      {unhealthyIntegrations.length > 0 && (
        <Alert color="orange" icon={<IconAlertTriangle />} mb="md">
          {unhealthyIntegrations.length} unhealthy integration(s) detected
        </Alert>
      )}

      {needsRotation.length > 0 && (
        <Alert color="yellow" icon={<IconKey />} mb="md">
          {needsRotation.length} integration(s) need credential rotation
        </Alert>
      )}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconActivity size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value="unhealthy"
            leftSection={<IconAlertTriangle size={16} />}
            rightSection={unhealthyCount > 0 ? <Badge color="red" size="sm">{unhealthyCount}</Badge> : null}
          >
            Unhealthy
          </Tabs.Tab>
          <Tabs.Tab
            value="circuit-breakers"
            leftSection={<IconCircleOff size={16} />}
            rightSection={openCircuitBreakers.length > 0 ? <Badge color="red" size="sm">{openCircuitBreakers.length}</Badge> : null}
          >
            Circuit Breakers
          </Tabs.Tab>
          <Tabs.Tab
            value="credentials"
            leftSection={<IconKey size={16} />}
            rightSection={needsRotation.length > 0 ? <Badge color="yellow" size="sm">{needsRotation.length}</Badge> : null}
          >
            Credentials
          </Tabs.Tab>
          <Tabs.Tab value="all" leftSection={<IconPlugConnected size={16} />}>
            All Integrations
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <OverviewTab
            healthyCount={healthyCount}
            degradedCount={degradedCount}
            unhealthyCount={unhealthyCount}
            totalCount={totalCount}
            healthPercentage={healthPercentage}
            openCircuitBreakers={openCircuitBreakers.length}
            needsRotation={needsRotation.length}
            loading={isLoading}
          />
        </Tabs.Panel>

        <Tabs.Panel value="unhealthy" pt="md">
          <IntegrationTable
            integrations={unhealthyIntegrations}
            loading={unhealthyLoading}
            onTest={handleTestConnection}
            onResetCircuitBreaker={handleResetCircuitBreaker}
            testing={testing}
            resetting={resetting}
            emptyMessage="All integrations are healthy"
          />
        </Tabs.Panel>

        <Tabs.Panel value="circuit-breakers" pt="md">
          <IntegrationTable
            integrations={openCircuitBreakers}
            loading={circuitBreakerLoading}
            onTest={handleTestConnection}
            onResetCircuitBreaker={handleResetCircuitBreaker}
            testing={testing}
            resetting={resetting}
            emptyMessage="No open circuit breakers"
            showResetAction
          />
        </Tabs.Panel>

        <Tabs.Panel value="credentials" pt="md">
          <CredentialRotationTable
            integrations={needsRotation}
            loading={rotationLoading}
            onRotate={handleRotateCredentials}
            rotating={rotating}
          />
        </Tabs.Panel>

        <Tabs.Panel value="all" pt="md">
          <IntegrationTable
            integrations={allIntegrations}
            loading={allLoading}
            onTest={handleTestConnection}
            onResetCircuitBreaker={handleResetCircuitBreaker}
            testing={testing}
            resetting={resetting}
            emptyMessage="No integrations configured"
          />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

interface OverviewTabProps {
  healthyCount: number;
  degradedCount: number;
  unhealthyCount: number;
  totalCount: number;
  healthPercentage: number;
  openCircuitBreakers: number;
  needsRotation: number;
  loading: boolean;
}

function OverviewTab({
  healthyCount,
  degradedCount,
  unhealthyCount,
  totalCount,
  healthPercentage,
  openCircuitBreakers,
  needsRotation,
  loading,
}: OverviewTabProps) {
  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader size="lg" />
        <Text c="dimmed">Loading integration status...</Text>
      </Stack>
    );
  }

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Card withBorder>
          <Group>
            <RingProgress
              size={80}
              thickness={8}
              sections={[
                { value: healthPercentage, color: 'green' },
                { value: Math.round((degradedCount / Math.max(totalCount, 1)) * 100), color: 'yellow' },
                { value: Math.round((unhealthyCount / Math.max(totalCount, 1)) * 100), color: 'red' },
              ]}
              label={
                <Text ta="center" size="sm" fw={700}>
                  {healthPercentage}%
                </Text>
              }
            />
            <div>
              <Text size="sm" c="dimmed">Overall Health</Text>
              <Text size="xl" fw={700}>{healthyCount}/{totalCount}</Text>
              <Text size="xs" c="dimmed">integrations healthy</Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color="green" variant="light">
              <IconCheck size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Healthy</Text>
              <Text size="xl" fw={700} c="green">{healthyCount}</Text>
              <Text size="xs" c="dimmed">operating normally</Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color="yellow" variant="light">
              <IconAlertTriangle size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Degraded</Text>
              <Text size="xl" fw={700} c="yellow">{degradedCount}</Text>
              <Text size="xs" c="dimmed">performance issues</Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color="red" variant="light">
              <IconX size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Unhealthy</Text>
              <Text size="xl" fw={700} c="red">{unhealthyCount}</Text>
              <Text size="xs" c="dimmed">require attention</Text>
            </div>
          </Group>
        </Card>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color={openCircuitBreakers > 0 ? 'red' : 'gray'} variant="light">
              <IconCircleOff size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Open Circuit Breakers</Text>
              <Text size="xl" fw={700} c={openCircuitBreakers > 0 ? 'red' : undefined}>
                {openCircuitBreakers}
              </Text>
              <Text size="xs" c="dimmed">
                {openCircuitBreakers > 0 ? 'blocking requests' : 'all circuits closed'}
              </Text>
            </div>
          </Group>
        </Card>

        <Card withBorder>
          <Group>
            <ThemeIcon size={60} radius="md" color={needsRotation > 0 ? 'yellow' : 'gray'} variant="light">
              <IconKey size={30} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Credential Rotation Due</Text>
              <Text size="xl" fw={700} c={needsRotation > 0 ? 'yellow' : undefined}>
                {needsRotation}
              </Text>
              <Text size="xs" c="dimmed">
                {needsRotation > 0 ? 'rotation recommended' : 'all credentials current'}
              </Text>
            </div>
          </Group>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}

interface IntegrationTableProps {
  integrations: Array<{
    integrationId: string;
    integrationName: string;
    integrationCode?: string;
    integrationType?: string;
    currentHealthStatus?: IntegrationHealthStatus | string;
    circuitBreakerState?: CircuitBreakerState | string;
    consecutiveFailures?: number;
    avgResponseTimeMs?: number;
    lastHealthCheckAt?: string;
    description?: string;
  }>;
  loading: boolean;
  onTest: (integrationId: string) => void;
  onResetCircuitBreaker: (integrationId: string) => void;
  testing: boolean;
  resetting: boolean;
  emptyMessage: string;
  showResetAction?: boolean;
}

function IntegrationTable({
  integrations,
  loading,
  onTest,
  onResetCircuitBreaker,
  testing,
  resetting,
  emptyMessage,
  showResetAction,
}: IntegrationTableProps) {
  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
        <Text c="dimmed">Loading...</Text>
      </Stack>
    );
  }

  if (integrations.length === 0) {
    return (
      <Alert color="green" icon={<IconShieldCheck />}>
        {emptyMessage}
      </Alert>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Type</Table.Th>
          <Table.Th>Health</Table.Th>
          <Table.Th>Circuit Breaker</Table.Th>
          <Table.Th>Failures</Table.Th>
          <Table.Th>Response Time</Table.Th>
          <Table.Th>Last Check</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {integrations.map((integration) => (
          <Table.Tr key={integration.integrationId}>
            <Table.Td>
              <Text fw={500}>{integration.integrationName}</Text>
              {integration.description && (
                <Text size="xs" c="dimmed">{integration.description}</Text>
              )}
            </Table.Td>
            <Table.Td>
              <Badge variant="light">{integration.integrationType}</Badge>
            </Table.Td>
            <Table.Td>
              {getHealthBadge(integration.currentHealthStatus ?? 'UNKNOWN')}
            </Table.Td>
            <Table.Td>
              {getCircuitBreakerBadge(integration.circuitBreakerState ?? 'UNKNOWN')}
            </Table.Td>
            <Table.Td>
              <Text c={integration.consecutiveFailures && integration.consecutiveFailures > 0 ? 'red' : undefined}>
                {integration.consecutiveFailures ?? 0}
              </Text>
            </Table.Td>
            <Table.Td>
              {integration.avgResponseTimeMs != null ? (
                <Text c={integration.avgResponseTimeMs > 1000 ? 'orange' : undefined}>
                  {integration.avgResponseTimeMs.toFixed(0)}ms
                </Text>
              ) : (
                <Text c="dimmed">-</Text>
              )}
            </Table.Td>
            <Table.Td>
              {integration.lastHealthCheckAt ? (
                <Text size="sm">{new Date(integration.lastHealthCheckAt).toLocaleString()}</Text>
              ) : (
                <Text c="dimmed" size="sm">Never</Text>
              )}
            </Table.Td>
            <Table.Td>
              <Group gap="xs">
                <Tooltip label="Test Connection">
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => onTest(integration.integrationId)}
                    loading={testing}
                  >
                    <IconPlugConnected size={16} />
                  </ActionIcon>
                </Tooltip>
                {(showResetAction || integration.circuitBreakerState === CircuitBreakerState.OPEN) && (
                  <Tooltip label="Reset Circuit Breaker">
                    <ActionIcon
                      variant="light"
                      color="green"
                      onClick={() => onResetCircuitBreaker(integration.integrationId)}
                      loading={resetting}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

interface CredentialRotationTableProps {
  integrations: Array<{
    integrationId: string;
    integrationName: string;
    description?: string;
    credentialRotationDueAt?: string;
    lastCredentialRotationAt?: string;
  }>;
  loading: boolean;
  onRotate: (integrationId: string) => void;
  rotating: boolean;
}

function CredentialRotationTable({
  integrations,
  loading,
  onRotate,
  rotating,
}: CredentialRotationTableProps) {
  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
        <Text c="dimmed">Loading...</Text>
      </Stack>
    );
  }

  if (integrations.length === 0) {
    return (
      <Alert color="green" icon={<IconShieldCheck />}>
        All credentials are current - no rotation needed
      </Alert>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Rotation Due</Table.Th>
          <Table.Th>Last Rotated</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {integrations.map((integration) => (
          <Table.Tr key={integration.integrationId}>
            <Table.Td>
              <Text fw={500}>{integration.integrationName}</Text>
              {integration.description && (
                <Text size="xs" c="dimmed">{integration.description}</Text>
              )}
            </Table.Td>
            <Table.Td>
              {integration.credentialRotationDueAt ? (
                <Text c="orange">{new Date(integration.credentialRotationDueAt).toLocaleDateString()}</Text>
              ) : (
                <Text c="dimmed">Not set</Text>
              )}
            </Table.Td>
            <Table.Td>
              {integration.lastCredentialRotationAt ? (
                <Text size="sm">{new Date(integration.lastCredentialRotationAt).toLocaleString()}</Text>
              ) : (
                <Text c="dimmed" size="sm">Never</Text>
              )}
            </Table.Td>
            <Table.Td>
              <Tooltip label="Rotate Credentials">
                <ActionIcon
                  variant="light"
                  color="orange"
                  onClick={() => onRotate(integration.integrationId)}
                  loading={rotating}
                >
                  <IconKey size={16} />
                </ActionIcon>
              </Tooltip>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

export default IntegrationStatusPage;
